<template>
  <div class="actions-manager">
    <div class="header">
      <h2>Actions Manager</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        Create New Action
      </button>
    </div>

    <div class="explanation-box mb-4">
      <h4>About Actions</h4>
      <p>Actions are automated tasks that can be triggered by workflow checks. They help automate responses to document analysis results and maintain consistency in your review process.</p>
      
      <h5>Key Concepts:</h5>
      <ul>
        <li><strong>Name:</strong> A descriptive name for the action (e.g., "Send Notification", "Create Task")</li>
        <li><strong>Type:</strong> The type of action to perform (e.g., "notification", "task_creation", "api_call")</li>
        <li><strong>Configuration:</strong> JSON configuration specific to the action type</li>
      </ul>

      <h5>Common Use Cases:</h5>
      <ul>
        <li><strong>Notifications:</strong> Send alerts when critical issues are found</li>
        <li><strong>Task Creation:</strong> Create follow-up tasks for manual review</li>
        <li><strong>API Integration:</strong> Trigger updates in external systems</li>
        <li><strong>Document Updates:</strong> Update document metadata or status</li>
      </ul>
    </div>

    <!-- Actions List -->
    <div class="actions-list">
      <table class="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Description</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="action in actions" :key="action.id">
            <td>{{ action.name }}</td>
            <td>{{ action.action_type }}</td>
            <td>{{ action.description }}</td>
            <td>{{ formatDate(action.created_at) }}</td>
            <td>
              <button @click="editAction(action)" class="btn btn-sm btn-outline-primary me-2">
                Edit
              </button>
              <button @click="confirmDelete(action)" class="btn btn-sm btn-outline-danger">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create/Edit Modal -->
    <Modal v-if="showCreateModal || editingAction" :show="true" @close="closeModal">
      <template #header>
        <h3>{{ editingAction ? 'Edit Action' : 'Create New Action' }}</h3>
      </template>
      
      <template #body>
        <form @submit.prevent="saveAction" class="action-form">
          <div class="mb-3">
            <label for="name" class="form-label">Name</label>
            <input
              v-model="actionForm.name"
              type="text"
              class="form-control"
              id="name"
              required
              placeholder="e.g., Send Critical Issue Alert"
            >
          </div>

          <div class="mb-3">
            <label for="action_type" class="form-label">Action Type</label>
            <select
              v-model="actionForm.action_type"
              class="form-select"
              id="action_type"
              required
              @change="updateConfigTemplate"
            >
              <option value="">Select an action type</option>
              <option value="NOTIFICATION">Notification</option>
              <option value="TASK_CREATION">Task Creation</option>
              <option value="API_CALL">API Call</option>
              <option value="DOCUMENT_UPDATE">Document Update</option>
              <option value="CUSTOM">Custom Action</option>
            </select>
          </div>

          <div class="mb-3">
            <label for="description" class="form-label">Description</label>
            <textarea
              v-model="actionForm.description"
              class="form-control"
              id="description"
              rows="2"
              placeholder="Explain what this action does and when it should be triggered"
            ></textarea>
          </div>

          <div class="mb-3">
            <label for="configuration" class="form-label">Configuration</label>
            <div class="config-helper mb-2">
              <div class="template-selector" v-if="configTemplates[actionForm.action_type]">
                <label class="form-label">Quick Templates:</label>
                <select class="form-select" @change="applyTemplate($event.target.value)">
                  <option value="">Select a template...</option>
                  <option v-for="(template, key) in configTemplates[actionForm.action_type]" 
                          :key="key" 
                          :value="key">
                    {{ template.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="config-editor">
              <div class="editor-toolbar">
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="formatJSON">
                  Format JSON
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="validateJSON">
                  Validate
                </button>
              </div>
              <textarea
                v-model="configurationText"
                class="form-control code-editor"
                id="configuration"
                rows="8"
                :placeholder="getConfigPlaceholder()"
                @input="validateConfiguration"
              ></textarea>
            </div>
            <div v-if="configError" class="text-danger mt-1">
              {{ configError }}
            </div>
            <div class="config-preview mt-2" v-if="isValidJSON">
              <h6>Configuration Preview:</h6>
              <pre class="preview-content">{{ prettyConfig }}</pre>
            </div>
            <div class="config-help mt-2">
              <h6>Configuration Help:</h6>
              <div v-html="getConfigHelp()"></div>
            </div>
          </div>
        </form>
      </template>

      <template #footer>
        <button @click="closeModal" class="btn btn-secondary me-2">Cancel</button>
        <button @click="saveAction" class="btn btn-primary" :disabled="!!configError">Save</button>
      </template>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal v-if="showDeleteModal" :show="true" @close="showDeleteModal = false">
      <template #header>
        <h3>Confirm Delete</h3>
      </template>
      
      <template #body>
        <p>Are you sure you want to delete the action "{{ actionToDelete?.name }}"?</p>
      </template>

      <template #footer>
        <button @click="showDeleteModal = false" class="btn btn-secondary me-2">Cancel</button>
        <button @click="deleteAction" class="btn btn-danger">Delete</button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import Modal from './Modal.vue'
import api from '../services/api'

export default {
  name: 'ActionsManager',
  components: { Modal },

  setup() {
    const actions = ref([])
    const showCreateModal = ref(false)
    const showDeleteModal = ref(false)
    const editingAction = ref(null)
    const actionToDelete = ref(null)
    const configError = ref('')
    const isValidJSON = ref(true)

    const actionForm = ref({
      name: '',
      action_type: '',
      description: '',
      configuration: {}
    })

    const configurationText = ref('{}')

    const configTemplates = {
      NOTIFICATION: {
        email: {
          name: 'Email Notification',
          config: {
            type: 'email',
            recipient_roles: ['RISK_MANAGER', 'ANALYST'],
            subject: 'Document Review Alert: {document_name}',
            message: 'A document requires your attention.\n\nAnalysis Results:\n{analysis_summary}\n\nFailed Checks:\n{failed_checks}\n\nRecommended Actions:\n{recommended_actions}',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CLIENT_MANAGER'],
            priority: 'normal'
          }
        },
        endorsement_missing: {
          name: 'Missing Endorsement Alert',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Required Endorsement Missing: {document_name}',
            message: 'Our review has identified missing required endorsements in your insurance documentation.\n\nPolicy: {document_name}\n\nMissing Endorsements:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nPlease provide updated documentation including all required endorsements. The additional insured endorsement must be provided on form CG 20 10 or equivalent.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        additional_insured_deficiency: {
          name: 'Additional Insured Endorsement Issue',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Additional Insured Endorsement Issue: {document_name}',
            message: 'The additional insured endorsement provided does not meet requirements.\n\nPolicy: {document_name}\n\nIssues Found:\n{failed_checks}\n\nRequired Updates:\n{recommended_actions}\n\nNote: Additional Insured status must be provided using the correct endorsement form (CG 20 10 or equivalent) and must include both ongoing and completed operations coverage.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER', 'RISK_MANAGER'],
            priority: 'high'
          }
        },
        workers_comp_state_specific: {
          name: 'Workers Compensation State Requirements',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'State-Specific Workers Compensation Requirements: {document_name}',
            message: 'Your workers compensation coverage requires state-specific updates.\n\nPolicy: {document_name}\n\nState Requirements:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nNote: Some states (OH, WA, WY, ND) require state-operated workers compensation coverage. Please ensure compliance with all state-specific requirements.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['RISK_MANAGER'],
            priority: 'high'
          }
        },
        policy_limit_deficiency: {
          name: 'Policy Limits Alert',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Insurance Policy Limits Below Requirements: {document_name}',
            message: 'Your insurance policy limits do not meet minimum requirements.\n\nPolicy: {document_name}\n\nLimit Deficiencies:\n{failed_checks}\n\nRequired Updates:\n{recommended_actions}\n\nPlease provide updated documentation showing the required coverage limits.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        waiver_of_subrogation: {
          name: 'Waiver of Subrogation Issue',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Waiver of Subrogation Requirement: {document_name}',
            message: 'The required waiver of subrogation endorsement is missing or incomplete.\n\nPolicy: {document_name}\n\nIssues Found:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nNote: Waiver of subrogation must be provided in favor of the certificate holder and all required parties.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        primary_noncontributory: {
          name: 'Primary & Non-Contributory Issue',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Primary & Non-Contributory Requirement: {document_name}',
            message: 'The required primary and non-contributory endorsement is missing or incomplete.\n\nPolicy: {document_name}\n\nIssues Found:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nNote: Coverage must be specifically endorsed to be primary and non-contributory to any similar coverage carried by the certificate holder.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        notice_of_cancellation: {
          name: 'Notice of Cancellation Issue',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Notice of Cancellation Requirement: {document_name}',
            message: 'The required notice of cancellation endorsement is missing or inadequate.\n\nPolicy: {document_name}\n\nIssues Found:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nNote: A minimum of 30 days notice of cancellation (10 days for non-payment) must be provided and specifically endorsed to the policy.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER', 'RISK_MANAGER'],
            priority: 'high'
          }
        },
        umbrella_follow_form: {
          name: 'Umbrella Follow Form Issue',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Umbrella Follow Form Requirement: {document_name}',
            message: 'The umbrella/excess policy requires follow form confirmation.\n\nPolicy: {document_name}\n\nIssues Found:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nNote: Umbrella/Excess coverage must be follow form over all primary policies and include all required endorsements.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        per_project_aggregate: {
          name: 'Per Project Aggregate Issue',
          config: {
            type: 'email',
            recipient_roles: ['INSURANCE_AGENT', 'SUBCONTRACTOR'],
            subject: 'Per Project Aggregate Requirement: {document_name}',
            message: 'The required per project aggregate endorsement is missing or incomplete.\n\nPolicy: {document_name}\n\nIssues Found:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nNote: General Liability policy must include per project aggregate endorsement CG 25 03 or equivalent.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        email_critical: {
          name: 'Critical Issue Email',
          config: {
            type: 'email',
            recipient_roles: ['RISK_MANAGER'],
            subject: 'CRITICAL: Analysis Failures in {document_name}',
            message: 'Critical issues have been identified in document {document_name}.\n\nAnalysis Summary:\n{analysis_summary}\n\nCritical Failures:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nPlease review and address these issues as soon as possible.',
            include_document_link: true,
            include_analysis_details: true,
            include_check_outputs: true,
            cc_roles: ['ADMIN'],
            priority: 'high'
          }
        },
        certificate_expiration: {
          name: 'Certificate Expiration Alert',
          config: {
            type: 'email',
            recipient_roles: ['CERTIFICATE_HOLDER', 'INSURANCE_AGENT'],
            subject: 'Insurance Certificate Expiration Notice: {document_name}',
            message: 'Your insurance certificate is approaching expiration or has coverage issues.\n\nCertificate Details:\n{document_name}\n\nAnalysis Results:\n{analysis_summary}\n\nIdentified Issues:\n{failed_checks}\n\nRequired Actions:\n{recommended_actions}\n\nPlease provide updated documentation addressing these issues.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['RISK_MANAGER'],
            priority: 'high'
          }
        },
        coverage_deficiency: {
          name: 'Coverage Deficiency Notice',
          config: {
            type: 'email',
            recipient_roles: ['SUBCONTRACTOR', 'INSURANCE_AGENT'],
            subject: 'Insurance Coverage Deficiency: {document_name}',
            message: 'Our analysis has identified coverage deficiencies in your insurance documentation.\n\nDocument: {document_name}\n\nDeficiencies Found:\n{failed_checks}\n\nRequired Coverage Updates:\n{recommended_actions}\n\nPlease work with your insurance agent to address these issues.',
            include_document_link: true,
            include_analysis_details: true,
            cc_roles: ['CERTIFICATE_HOLDER'],
            priority: 'high'
          }
        },
        slack: {
          name: 'Slack Notification',
          config: {
            type: 'slack',
            channel: '#insurance-alerts',
            message: '*Insurance Certificate Alert*\nDocument: {document_name}\n\nAnalysis Summary:\n{analysis_summary}\n\nFailed Checks:\n{failed_checks}',
            mention_roles: ['RISK_MANAGER', 'INSURANCE_AGENT'],
            include_analysis_details: true,
            priority: 'normal'
          }
        }
      },
      TASK_CREATION: {
        review_task: {
          name: 'Review Task',
          config: {
            title: 'Review Document {document_name}',
            description: 'Please review this document for compliance issues.',
            priority: 'high',
            due_days: 3,
            assignee: 'reviewer@example.com'
          }
        }
      },
      API_CALL: {
        webhook: {
          name: 'Webhook POST',
          config: {
            url: 'https://api.example.com/webhook',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer {api_key}'
            },
            body: {
              document_id: '{document_id}',
              status: '{status}',
              message: '{message}'
            }
          }
        }
      },
      DOCUMENT_UPDATE: {
        status_update: {
          name: 'Update Document Status',
          config: {
            fields: {
              status: 'reviewed',
              review_date: '{current_date}',
              reviewer: '{user_email}'
            }
          }
        }
      }
    }

    const prettyConfig = computed(() => {
      try {
        return JSON.stringify(JSON.parse(configurationText.value), null, 2)
      } catch {
        return configurationText.value
      }
    })

    const loadActions = async () => {
      try {
        const response = await api.get('/actions/')
        actions.value = response.data
      } catch (error) {
        console.error('Error loading actions:', error)
      }
    }

    const validateConfiguration = () => {
      try {
        JSON.parse(configurationText.value)
        configError.value = ''
        isValidJSON.value = true
      } catch (error) {
        configError.value = 'Invalid JSON configuration'
        isValidJSON.value = false
      }
    }

    const formatJSON = () => {
      try {
        const parsed = JSON.parse(configurationText.value)
        configurationText.value = JSON.stringify(parsed, null, 2)
        validateConfiguration()
      } catch (error) {
        // If we can't parse it, leave it as is
      }
    }

    const validateJSON = () => {
      validateConfiguration()
      if (!configError.value) {
        const parsed = JSON.parse(configurationText.value)
        // Add more specific validation based on action type
        if (actionForm.value.action_type === 'NOTIFICATION') {
          if (!parsed.type) {
            configError.value = 'Notification configuration must include a "type" field'
          }
          if (parsed.type === 'email') {
            if (!parsed.recipient_roles || !Array.isArray(parsed.recipient_roles) || parsed.recipient_roles.length === 0) {
              configError.value = 'Email notifications must include at least one recipient role'
            }
            if (parsed.cc_roles && !Array.isArray(parsed.cc_roles)) {
              configError.value = 'CC roles must be an array'
            }
          }
          if (parsed.type === 'slack') {
            if (!parsed.channel) {
              configError.value = 'Slack notifications must include a channel'
            }
            if (parsed.mention_roles && !Array.isArray(parsed.mention_roles)) {
              configError.value = 'Mention roles must be an array'
            }
          }
        }
        // Add more type-specific validation as needed
      }
    }

    const updateConfigTemplate = () => {
      const actionType = actionForm.value.action_type
      if (actionType && configTemplates[actionType]) {
        // Get the first template for this action type
        const firstTemplate = Object.values(configTemplates[actionType])[0]
        if (firstTemplate) {
          configurationText.value = JSON.stringify(firstTemplate.config, null, 2)
          validateConfiguration()
        }
      }
    }

    const applyTemplate = (templateKey) => {
      if (!templateKey) return
      
      const actionType = actionForm.value.action_type
      const template = configTemplates[actionType]?.[templateKey]
      
      if (template) {
        configurationText.value = JSON.stringify(template.config, null, 2)
        validateConfiguration()
      }
    }

    const getConfigPlaceholder = () => {
      const type = actionForm.value.action_type
      switch (type) {
        case 'NOTIFICATION':
          return 'Enter notification configuration (recipients, message, etc.)'
        case 'TASK_CREATION':
          return 'Enter task configuration (title, assignee, due date, etc.)'
        case 'API_CALL':
          return 'Enter API call configuration (URL, method, headers, body)'
        case 'DOCUMENT_UPDATE':
          return 'Enter document update configuration (fields to update)'
        default:
          return 'Enter JSON configuration for this action'
      }
    }

    const getConfigHelp = () => {
      const type = actionForm.value.action_type
      switch (type) {
        case 'NOTIFICATION':
          return `
            <p>Available variables:</p>
            <ul>
              <li><code>{document_name}</code> - Name of the document</li>
              <li><code>{document_id}</code> - Document identifier</li>
              <li><code>{status}</code> - Current document status</li>
              <li><code>{user_email}</code> - Current user's email</li>
              <li><code>{analysis_summary}</code> - Summary of the analysis results</li>
              <li><code>{failed_checks}</code> - List of failed automated checks</li>
              <li><code>{recommended_actions}</code> - Recommended actions based on failed checks</li>
              <li><code>{check_outputs}</code> - Detailed output from each automated check</li>
            </ul>
            <p>Analysis Options:</p>
            <ul>
              <li><code>include_analysis_details</code> - Include full analysis results (default: false)</li>
              <li><code>include_check_outputs</code> - Include detailed check outputs (default: false)</li>
            </ul>
            <p>Role-based Recipients:</p>
            <ul>
              <li><code>recipient_roles</code> - Array of roles to receive the notification</li>
              <li><code>cc_roles</code> - Array of roles to CC on email notifications</li>
              <li><code>mention_roles</code> - Array of roles to mention in Slack notifications</li>
            </ul>
            <p>Available Roles:</p>
            <ul>
              <li><code>ADMIN</code> - Administrator</li>
              <li><code>RISK_MANAGER</code> - Risk Manager</li>
              <li><code>ANALYST</code> - Risk Analyst</li>
              <li><code>CLIENT_MANAGER</code> - Client Manager</li>
              <li><code>REVIEWER</code> - Document Reviewer</li>
              <li><code>READ_ONLY</code> - Read Only User</li>
              <li><code>CERTIFICATE_HOLDER</code> - Certificate Holder</li>
              <li><code>SUBCONTRACTOR</code> - Subcontractor</li>
              <li><code>INSURANCE_AGENT</code> - Insurance Agent</li>
            </ul>
          `
        case 'TASK_CREATION':
          return `
            <p>Required fields:</p>
            <ul>
              <li><code>title</code> - Task title</li>
              <li><code>description</code> - Task description</li>
              <li><code>assignee</code> - Email of the assignee</li>
            </ul>
          `
        case 'API_CALL':
          return `
            <p>Required fields:</p>
            <ul>
              <li><code>url</code> - API endpoint URL</li>
              <li><code>method</code> - HTTP method (GET, POST, etc.)</li>
              <li><code>headers</code> - Request headers object</li>
            </ul>
          `
        case 'DOCUMENT_UPDATE':
          return `
            <p>Required fields:</p>
            <ul>
              <li><code>fields</code> - Object containing field names and values to update</li>
            </ul>
          `
        default:
          return '<p>Select an action type to see configuration help.</p>'
      }
    }

    const saveAction = async () => {
      try {
        validateJSON()
        if (configError.value) return

        const payload = {
          ...actionForm.value,
          configuration: JSON.parse(configurationText.value)
        }

        if (editingAction.value) {
          await api.put(`/actions/${editingAction.value.id}/`, payload)
        } else {
          await api.post('/actions/', payload)
        }

        await loadActions()
        closeModal()
      } catch (error) {
        console.error('Error saving action:', error)
      }
    }

    const editAction = (action) => {
      editingAction.value = action
      actionForm.value = {
        name: action.name,
        action_type: action.action_type,
        description: action.description || '',
      }
      configurationText.value = JSON.stringify(action.configuration || {}, null, 2)
      validateConfiguration()
    }

    const confirmDelete = (action) => {
      actionToDelete.value = action
      showDeleteModal.value = true
    }

    const deleteAction = async () => {
      if (!actionToDelete.value) return

      try {
        await api.delete(`/actions/${actionToDelete.value.id}/`)
        await loadActions()
        showDeleteModal.value = false
        actionToDelete.value = null
      } catch (error) {
        console.error('Error deleting action:', error)
      }
    }

    const closeModal = () => {
      showCreateModal.value = false
      editingAction.value = null
      actionToDelete.value = null
      actionForm.value = {
        name: '',
        action_type: '',
        description: '',
      }
      configurationText.value = '{}'
      configError.value = ''
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleString()
    }

    onMounted(() => {
      loadActions()
    })

    return {
      actions,
      showCreateModal,
      showDeleteModal,
      editingAction,
      actionToDelete,
      actionForm,
      configurationText,
      configError,
      isValidJSON,
      configTemplates,
      prettyConfig,
      saveAction,
      editAction,
      confirmDelete,
      deleteAction,
      closeModal,
      formatDate,
      validateConfiguration,
      formatJSON,
      validateJSON,
      updateConfigTemplate,
      applyTemplate,
      getConfigPlaceholder,
      getConfigHelp
    }
  }
}
</script>

<style scoped>
.actions-manager {
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  background-color: #f3f4f6;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.header h2 {
  margin: 0;
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
}

.explanation-box {
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.explanation-box h4 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  color: #111827;
}

.explanation-box h5 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: #374151;
}

.explanation-box p {
  margin-bottom: 1rem;
  color: #4b5563;
}

.explanation-box ul {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.explanation-box li {
  margin-bottom: 0.5rem;
  color: #4b5563;
}

.actions-list {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
}

.table th {
  background-color: #f9fafb;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
}

.config-helper {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 1rem;
}

.template-selector {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.editor-toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.code-editor {
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  tab-size: 2;
}

.config-preview {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 1rem;
}

.preview-content {
  margin: 0;
  font-family: monospace;
  font-size: 0.875rem;
  white-space: pre-wrap;
}

.config-help {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 1rem;
}

.config-help h6 {
  margin-top: 0;
  margin-bottom: 0.75rem;
  color: #374151;
}

.config-help ul {
  margin: 0;
  padding-left: 1.5rem;
}

.config-help li {
  margin-bottom: 0.5rem;
  color: #4b5563;
}

.config-help code {
  background-color: #e5e7eb;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: #374151;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
}

.btn-primary {
  background-color: #3b82f6;
  border: 1px solid transparent;
  color: white;
}

.btn-primary:hover {
  background-color: #2563eb;
}

.btn-secondary {
  background-color: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-secondary:hover {
  background-color: #f9fafb;
}

.btn-danger {
  background-color: #ef4444;
  border: 1px solid transparent;
  color: white;
}

.btn-danger:hover {
  background-color: #dc2626;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.btn-outline-primary {
  color: #3b82f6;
  border: 1px solid #3b82f6;
  background-color: transparent;
}

.btn-outline-primary:hover {
  background-color: #3b82f6;
  color: white;
}

.btn-outline-danger {
  color: #ef4444;
  border: 1px solid #ef4444;
  background-color: transparent;
}

.btn-outline-danger:hover {
  background-color: #ef4444;
  color: white;
}

.btn-outline-secondary {
  color: #6b7280;
  border: 1px solid #6b7280;
  background-color: transparent;
}

.btn-outline-secondary:hover {
  background-color: #6b7280;
  color: white;
}

.me-2 {
  margin-right: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.form-label {
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  width: 100%;
  transition: border-color 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.text-danger {
  color: #ef4444;
}
</style> 